import { subCompanyCreditProviderApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const SUB_COMPANY_USER_CREDIT_PROVIDERS_ACTION_TYPE = createNamedActions(
  'subCompanyUserCreditProviders',
  {
    GET_USER_CREDIT_PROVIDERS: 'GET_USER_CREDIT_PROVIDERS',
    UPDATE_USER_CREDIT_PROVIDER: 'UPDATE_USER_CREDIT_PROVIDER',
    DELETE_USER_CREDIT_PROVIDER: 'DELETE_USER_CREDIT_PROVIDER',
    RESET_STATE: 'RESET_STATE',
  },
);

// A per-user GET that fails resolves to "no override" (falls back to the company default) rather
// than rejecting the whole batch — the shape mirrors the backend's own empty-DTO response.
const NO_OVERRIDE = { provider: null, userName: null, useTest: false, hasPassword: false };

// SubCompanyCreditProviderController only exposes a single-user GET (DEV-5596) — there is no
// bulk-list endpoint — so the User Credentials table's Source/Provider/Account columns resolve
// via one GET per subcompany user, run in parallel. Promise.allSettled (not Promise.all): one
// user's failed GET must not blank the whole table — every other user still populates.
export const getSubCompanyUserCreditProvidersAction = catchAsyncActionError(
  ({ subCompanyId, userIds }) =>
    async dispatch => {
      const results = await Promise.allSettled(
        userIds.map(userId =>
          subCompanyCreditProviderApi
            .getUserCreditProvider({ subCompanyId, userId })
            .then(({ data }) => data),
        ),
      );

      const entries = userIds.map((userId, index) => {
        const result = results[index];

        return [userId, result.status === 'fulfilled' ? result.value : NO_OVERRIDE];
      });

      return dispatch({
        type: SUB_COMPANY_USER_CREDIT_PROVIDERS_ACTION_TYPE.GET_USER_CREDIT_PROVIDERS,
        payload: Object.fromEntries(entries),
      });
    },
);

export const updateSubCompanyUserCreditProviderAction = catchAsyncActionError(
  ({ subCompanyId, userId, provider, userName, password, useTest }) =>
    async dispatch => {
      const { data } = await subCompanyCreditProviderApi.updateUserCreditProvider({
        subCompanyId,
        userId,
        provider,
        userName,
        password,
        useTest,
      });

      return dispatch({
        type: SUB_COMPANY_USER_CREDIT_PROVIDERS_ACTION_TYPE.UPDATE_USER_CREDIT_PROVIDER,
        payload: data,
        meta: { userId },
      });
    },
);

export const deleteSubCompanyUserCreditProviderAction = catchAsyncActionError(
  ({ subCompanyId, userId }) =>
    async dispatch => {
      await subCompanyCreditProviderApi.deleteUserCreditProvider({ subCompanyId, userId });

      return dispatch({
        type: SUB_COMPANY_USER_CREDIT_PROVIDERS_ACTION_TYPE.DELETE_USER_CREDIT_PROVIDER,
        meta: { userId },
      });
    },
);

export const resetSubCompanyUserCreditProvidersAction = () => ({
  type: SUB_COMPANY_USER_CREDIT_PROVIDERS_ACTION_TYPE.RESET_STATE,
});
